import { zodResolver } from '@hookform/resolvers/zod';
import { Combobox } from 'baseui/combobox';
import { StyledCaption } from 'baseui/form-control';
import { Spinner } from 'baseui/spinner';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Text as AriaText } from 'react-aria-components';
import { AlertCircle, CheckCircle } from 'react-feather';
import {
  Control,
  FieldPath,
  FieldValues,
  get,
  useForm,
  useFormState,
  get as getRHFFieldError,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod';

import { FormController } from 'components/FormController';
import { Input, InputProps } from 'components/Input';
import { CountrySelect } from 'features/CountrySelect';
import { StateSelect } from 'features/StateSelect';
import { getErrorMessage, isMutationSuccess } from 'services/api/base-api';
import {
  useCustomersAddressQuery,
  useCustomersQuery,
} from 'services/api/customers/endpoints';
import type { Customer } from 'services/api/customers/types';
import { Address, rawAddressSchema } from 'services/api/types.shared';
import { endpoints as UtilityAPIService } from 'services/api/utilities/endpoint';
import { store } from 'store/store';
import useDebounce from 'utils/hooks/useDebounce';
import { useLocale } from 'utils/hooks/useLocale';

const nonEmptyString = z
  .string()
  .trim()
  .min(1, { message: 'errors.invalid_type_received_undefined' });

export const customerSchema = z.object({
  id: z.number(),
  address: z.object({
    street: nonEmptyString,
    street2: z.string().trim().nullable().optional().default(''),
    city: nonEmptyString,
    state: nonEmptyString,
    postalCode: nonEmptyString,
    country: nonEmptyString,
    lng: z.number().refine(
      (val) => {
        return val !== 0;
      },
      { message: 'No coordinates set' },
    ),
    lat: z
      .number()
      .refine((val) => val !== 0, { message: 'No coordinates set' }),
    isVerified: z.boolean().optional().default(false),
    isVerifiying: z.boolean().optional(),
    timeZone: z.string().trim().nullable().optional().default(''),
  }),
  firstName: nonEmptyString,
  lastName: nonEmptyString,
  businessName: z.string().optional().nullish(),
  email: nonEmptyString,
  phone: nonEmptyString,
});

type FormFields = z.infer<typeof customerSchema>;

/**
 * https://github.com/react-hook-form/react-hook-form/issues/1005#issuecomment-988380981
 */
const stopPropagate = (callback: () => void) => {
  return (e: { stopPropagation: () => void; preventDefault: () => void }) => {
    e.stopPropagation();
    e.preventDefault();
    callback();
  };
};

const modifiableAddressSchema = rawAddressSchema.pick({
  street: true,
  street2: true,
  city: true,
  state: true,
  postalCode: true,
  country: true,
});
type AddressLike = z.infer<typeof modifiableAddressSchema>;

function debounce<T extends (...args: any[]) => void>(func: T, wait: number) {
  let timeout: ReturnType<typeof setTimeout>;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const geocodeAddress = async (address: Address | AddressLike) => {
  const formattedAddres = `${address.street} ${address.city} ${address.state} ${address.country}`;

  const result = await store.dispatch(
    UtilityAPIService.endpoints.geocode.initiate({ address: formattedAddres }),
  );
  if (isMutationSuccess(result)) {
    if (result.data.confidence >= 0.8) {
      return {
        latitude: result.data.latitude,
        longitude: result.data.longitude,
        timeZone: result.data.timeZone,
      };
    }
    throw new Error('No confident location found');
  } else {
    throw new Error(getErrorMessage(result.error));
  }
};

export default function BillToForm({
  id,
  initialValues,
  onSubmit: onSubmitHandler,
  onValidationChange,
}: {
  id?: string;
  initialValues?: Partial<FormFields>;
  onSubmit: (data: FormFields) => void;
  onValidationChange?: (isValid: boolean) => void;
}) {
  const { t } = useLocale();
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    setError,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm<FormFields>({
    defaultValues: initialValues,
    resolver: zodResolver(customerSchema),
  });

  // This tracks if a customer was actually selected from the search combobox or if the user manually inputted the customer details
  const [customerSelected, setCustomerSelected] = useState(false);
  const [originalValues] = useState(initialValues);
  const [selectedCustomerData, setSelectedCustomerData] =
    useState<FormFields | null>(null);

  const onSubmit = handleSubmit((data) => {
    // If no customer was selected, we reset the form to the original values
    if (!customerSelected && originalValues) {
      onSubmitHandler(originalValues as FormFields);
    } else {
      onSubmitHandler(data);
    }
  });

  const [isVerifying, setIsVerifying] = useState(false);

  function prefillCustomer(customer: Customer) {
    const { address } = customer;

    const isCustomerCompany = customer.firstName === customer.businessName;

    const customerData: FormFields = {
      id: customer.id,
      firstName: customer.firstName,
      lastName: isCustomerCompany ? 'PLACEHOLDER' : customer.lastName,
      email: customer.email,
      phone: customer.phone,
      businessName: customer.businessName,
      address: {
        ...address,
        isVerified: true,
      },
    };

    setValue('id', customerData.id, { shouldValidate: true });
    setValue('firstName', customerData.firstName, { shouldValidate: true });
    setValue('lastName', customerData.lastName, { shouldValidate: true });
    setValue('email', customerData.email, { shouldValidate: true });
    setValue('phone', customerData.phone, { shouldValidate: true });
    setValue('businessName', customerData.businessName, {
      shouldValidate: true,
    });
    setValue('address', customerData.address);

    setSelectedCustomerData(customerData);
    setCustomerSelected(true);
  }

  const isAddressVerified = watch('address.isVerified');
  const isVerifyingAddress = watch('address.isVerifiying');
  const selectedCountryCode = watch('address.country');
  const address = getValues('address');

  // Reset customer selection state when form is initialized with different data
  useEffect(() => {
    setCustomerSelected(false);
    setSelectedCustomerData(null);
    // Only reset when the customer ID changes
  }, [initialValues?.id]);

  // Check if current form values match the selected customer
  const formValues = watch();

  // Check if current values match the selected customer
  const currentValuesMatchSelectedCustomer = useCallback(() => {
    if (!selectedCustomerData) return false;

    // Helper function to normalize values for comparison
    // This handles null, undefined, and empty string
    const normalize = (val: any) =>
      val === null || val === undefined ? '' : String(val);

    const matches =
      normalize(formValues.firstName) ===
        normalize(selectedCustomerData.firstName) &&
      normalize(formValues.lastName) ===
        normalize(selectedCustomerData.lastName) &&
      normalize(formValues.email) === normalize(selectedCustomerData.email) &&
      normalize(formValues.phone) === normalize(selectedCustomerData.phone) &&
      normalize(formValues.address?.street) ===
        normalize(selectedCustomerData.address?.street) &&
      normalize(formValues.address?.street2) ===
        normalize(selectedCustomerData.address?.street2) &&
      normalize(formValues.address?.city) ===
        normalize(selectedCustomerData.address?.city) &&
      normalize(formValues.address?.state) ===
        normalize(selectedCustomerData.address?.state) &&
      normalize(formValues.address?.postalCode) ===
        normalize(selectedCustomerData.address?.postalCode) &&
      normalize(formValues.address?.country) ===
        normalize(selectedCustomerData.address?.country);

    return matches;
  }, [formValues, selectedCustomerData]);

  useEffect(() => {
    if (selectedCustomerData) {
      const valuesMatch = currentValuesMatchSelectedCustomer();
      setCustomerSelected(valuesMatch);
    }
  }, [selectedCustomerData, currentValuesMatchSelectedCustomer]);

  const currentValuesMatchInitial = useCallback(() => {
    if (!initialValues) return false;

    const normalize = (val: any) =>
      val === null || val === undefined ? '' : String(val);

    return (
      normalize(formValues.firstName) === normalize(initialValues.firstName) &&
      normalize(formValues.lastName) === normalize(initialValues.lastName) &&
      normalize(formValues.email) === normalize(initialValues.email) &&
      normalize(formValues.phone) === normalize(initialValues.phone) &&
      normalize(formValues.address?.street) ===
        normalize(initialValues.address?.street) &&
      normalize(formValues.address?.street2) ===
        normalize(initialValues.address?.street2) &&
      normalize(formValues.address?.city) ===
        normalize(initialValues.address?.city) &&
      normalize(formValues.address?.state) ===
        normalize(initialValues.address?.state) &&
      normalize(formValues.address?.postalCode) ===
        normalize(initialValues.address?.postalCode) &&
      normalize(formValues.address?.country) ===
        normalize(initialValues.address?.country)
    );
  }, [formValues, initialValues]);

  useEffect(() => {
    if (onValidationChange) {
      const valuesMatchSelected = currentValuesMatchSelectedCustomer();
      const valuesMatchInitial = currentValuesMatchInitial();
      const isValid = valuesMatchSelected || valuesMatchInitial;

      onValidationChange(isValid);
    }
  }, [
    onValidationChange,
    currentValuesMatchSelectedCustomer,
    currentValuesMatchInitial,
    isDirty,
    customerSelected,
    selectedCustomerData,
  ]);

  const handleAddressValidation = useCallback(
    async (address: Address) => {
      setIsVerifying(true);
      setValue('address.isVerifiying', true);
      setValue('address.isVerified', false);
      clearErrors('address.isVerified');
      clearErrors('address.lat');

      try {
        const coords = await geocodeAddress(address);

        if (coords) {
          setValue('address.lat', coords.latitude);
          setValue('address.lng', coords.longitude);
          setValue('address.timeZone', coords.timeZone);
          setValue('address.isVerified', true);
        } else {
          setValue('address.lat', 0);
          setValue('address.lng', 0);
          setValue('address.isVerified', false);
          setError('address.isVerified', {
            message:
              'The address you entered did not return any verified locations.',
          });
        }
      } catch (error) {
        setValue('address.lat', 0);
        setValue('address.lng', 0);
        setValue('address.isVerified', false);
        setError('address.isVerified', {
          message: 'An error occurred while verifying the address.',
        });
      } finally {
        setIsVerifying(false);
        setValue('address.isVerifiying', false);
      }
    },
    [setValue, clearErrors, setError],
  );

  useEffect(() => {
    const callback = debounce(
      async (
        data: Parameters<Parameters<typeof watch>[0]>[0],
        { name }: Parameters<Parameters<typeof watch>[0]>[1],
      ) => {
        const keysToMatch: FieldPath<FormFields>[] = [
          'address.street',
          'address.street2',
          'address.city',
          'address.state',
          'address.postalCode',
          'address.country',
        ];
        const isAddress = name && keysToMatch.includes(name);
        const validated = modifiableAddressSchema.safeParse(data);
        if (isAddress && validated.success) {
          setValue('address.isVerifiying', true);
          setValue('address.isVerified', false);
          clearErrors(['address.lat', 'address.lng', 'address.isVerified']);
          geocodeAddress(validated.data)
            .then((coordinates) => {
              clearErrors('address.isVerified');
              setValue('address.isVerified', true);
              setValue('address.lat', coordinates.latitude);
              setValue('address.lng', coordinates.longitude);
              setValue('address.timeZone', coordinates.timeZone);
            })
            .catch(() => {
              clearErrors('address.lat');
              clearErrors('address.lng');
              setValue('address.lat', 0);
              setValue('address.lng', 0);
              setValue('address.timeZone', '');
              setValue('address.isVerified', false);
              setError('address.isVerified', {
                // message:
                // 'The address you entered did not return any verified locations.',
                message: t('address_not_verified_message'),
              });
            })
            .finally(() => setValue('address.isVerifiying', false));
        }
      },
      950,
    );
    const { unsubscribe } = watch(callback);
    return () => unsubscribe();
  }, [watch, setValue, clearErrors, setError, t]);

  useEffect(() => {
    if (address) {
      const validated = modifiableAddressSchema.safeParse(address);

      if (validated.success) {
        handleAddressValidation(validated.data as Address);
      }
    }
  }, [address, handleAddressValidation]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      const addressFields = [
        'address.street',
        'address.street2',
        'address.city',
        'address.state',
        'address.postalCode',
        'address.country',
      ];

      if (name && addressFields.includes(name)) {
        const timer = setTimeout(() => {
          const address = getValues('address');
          const validated = modifiableAddressSchema.safeParse(address);

          if (validated.success && !isVerifying) {
            handleAddressValidation(validated.data as Address);
          }
        }, 950);

        return () => clearTimeout(timer);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, handleAddressValidation, isVerifying, getValues]);

  return (
    <form
      className="grid grid-cols-4 gap-x-4"
      id={id}
      onSubmit={stopPropagate(onSubmit)}
    >
      <div className="col-span-full mb-4">
        <h3 className="mb-2 text-lg font-semibold">{t('Customer Details')}</h3>
        {!customerSelected && (
          <p className="mb-4 text-sm text-gray-500">
            {t('bill_to_form_customer_selected_description')}
          </p>
        )}
      </div>

      {customerSelected && (
        <div className="col-span-full mb-2 flex items-center space-x-1 text-green-600">
          <CheckCircle className="h-4 w-4" />
          <span className="text-sm">
            {t('bill_to_form_customer_selected_tag')}
          </span>
        </div>
      )}

      <FormController
        control={control}
        name="firstName"
        label={t('First Name')}
        overrides={{ Root: { props: { className: 'col-span-2' } } }}
      >
        {({ field }) => (
          <CustomerSearchCombobox
            value={field.value}
            onSelect={(selected) => {
              prefillCustomer(selected);
              field.onChange(selected.firstName);
            }}
            onChange={(value) => {
              field.onChange(value);
            }}
            onManualInputToggled={() => {
              setValue('lastName', '');
              setValue('email', '');
              setValue('phone', '');
              setValue('address.street', '');
              setValue('address.street2', '');
              setValue('address.city', '');
              setValue('address.country', '');
              setValue('address.postalCode', '');
              setValue('address.state', '');
            }}
            performSearch={true}
          />
        )}
      </FormController>

      <FormController
        control={control}
        name="lastName"
        label={t('Last Name')}
        overrides={{ Root: { props: { className: 'col-span-2' } } }}
      >
        {({ field }) => (
          <CustomerSearchCombobox
            value={field.value}
            searchType="last-name"
            onSelect={(selected) => {
              prefillCustomer(selected);
              field.onChange(selected.lastName);
            }}
            onChange={(value) => {
              field.onChange(value);
            }}
            onManualInputToggled={() => {
              setValue('firstName', '');
              setValue('email', '');
              setValue('phone', '');
              setValue('address.street', '');
              setValue('address.street2', '');
              setValue('address.city', '');
              setValue('address.country', '');
              setValue('address.postalCode', '');
              setValue('address.state', '');
            }}
            performSearch={true}
          />
        )}
      </FormController>

      <FormController
        control={control}
        name="email"
        label={t('Email Address')}
        overrides={{ Root: { props: { className: 'col-span-2' } } }}
      >
        {({ field }) => (
          <CustomerSearchCombobox
            value={field.value}
            searchType="email"
            onSelect={(selected) => {
              prefillCustomer(selected);
              field.onChange(selected.firstName);
            }}
            onChange={(value) => {
              field.onChange(value);
            }}
            onManualInputToggled={() => {
              setValue('firstName', '');
              setValue('lastName', '');
              setValue('phone', '');
              setValue('address.street', '');
              setValue('address.street2', '');
              setValue('address.city', '');
              setValue('address.country', '');
              setValue('address.postalCode', '');
              setValue('address.state', '');
            }}
            performSearch={true}
          />
        )}
      </FormController>

      <FormController
        control={control}
        name="phone"
        label={t('Phone Number')}
        overrides={{ Root: { props: { className: 'col-span-2' } } }}
      >
        {({ field }) => (
          <CustomerSearchCombobox
            value={field.value}
            placeholder="000-000-0000"
            searchType="phone"
            onSelect={(selected) => {
              prefillCustomer(selected);
              field.onChange(selected.firstName);
            }}
            onChange={(value) => {
              field.onChange(value);
            }}
            onManualInputToggled={() => {
              setValue('firstName', '');
              setValue('lastName', '');
              setValue('email', '');
              setValue('address.street', '');
              setValue('address.street2', '');
              setValue('address.city', '');
              setValue('address.country', '');
              setValue('address.postalCode', '');
              setValue('address.state', '');
            }}
            performSearch={true}
          />
        )}
      </FormController>

      <div className="col-span-full mb-4 mt-6">
        <h3 className="mb-2 text-lg font-semibold">{t('Billing Address')}</h3>
      </div>

      <div className="col-span-full">
        {isAddressVerified && (
          <div className="flex items-center space-x-1 text-green-600">
            <span>
              <CheckCircle className="h-4 w-4" />
            </span>
            <p className="text-sm">{t('Verified')}</p>
          </div>
        )}
        <VerfyingAddress control={control} path="address.isVerifiying" />
        <RHFErrorMessageValue control={control} field="address.lat">
          {() => (
            <Alert>
              <AlertTitle>{t('Address not verified')}</AlertTitle>
              <AlertDescription>
                {t('address_not_verified_message')}
              </AlertDescription>
              <AlertActions>
                <button
                  type="button"
                  className="rounded-md bg-red-50 px-2 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
                  onClick={() => {
                    setValue('address.street', getValues('address.street'));
                  }}
                  disabled={isVerifyingAddress}
                >
                  {t('Verify Address')}
                </button>
              </AlertActions>
            </Alert>
          )}
        </RHFErrorMessageValue>
        <RHFErrorMessageValue control={control} field="address.isVerified">
          {(message) => (
            <Alert>
              <AlertTitle>{t('Address not verified')}</AlertTitle>
              <AlertDescription>
                {t('address_not_verified_message')}
              </AlertDescription>
            </Alert>
          )}
        </RHFErrorMessageValue>
      </div>

      <FormController
        control={control}
        name="address.street"
        label={t('Address')}
        overrides={{ Root: { props: { className: 'col-span-3' } } }}
      >
        {({ field }) => (
          <CustomerAddressSearchCombobox
            value={field.value}
            onSelect={(selected) => {
              prefillCustomer(selected);
              field.onChange(selected.address.street);
            }}
            onChange={(e) => {
              field.onChange(e);
              setValue('address.isVerified', false);
            }}
            onManualInputToggled={() => {
              setValue('lastName', '');
            }}
            performSearch={true}
          />
        )}
      </FormController>

      <FormController
        control={control}
        name="address.street2"
        label={t('Unit')}
        overrides={{ Root: { props: { className: 'col-span-1' } } }}
        defaultValue={''}
      >
        {({ field }) => (
          <CustomerAddressSearchCombobox
            value={field.value ?? ''}
            onSelect={(selected) => {
              prefillCustomer(selected);
              field.onChange(selected.address.street2 ?? '');
            }}
            onChange={(value) => {
              field.onChange(value);
            }}
            onManualInputToggled={() => {
              setValue('lastName', '');
            }}
            performSearch={true}
          />
        )}
      </FormController>

      <FormController
        control={control}
        name="address.city"
        label={t('City')}
        overrides={{ Root: { props: { className: 'col-span-2' } } }}
      >
        {({ field }) => <Input {...field} />}
      </FormController>

      <FormController
        control={control}
        name="address.country"
        label={t('Country')}
        overrides={{ Root: { props: { className: 'col-span-2' } } }}
      >
        {({ field }) => (
          <CountrySelect
            useClientCountryQuery={true}
            value={field.value}
            onChange={({ option }) => {
              field.onChange(option?.id);
            }}
          />
        )}
      </FormController>

      <FormController
        control={control}
        name="address.postalCode"
        label={t('Zip')}
        overrides={{ Root: { props: { className: 'col-span-2' } } }}
      >
        {({ field }) => <Input {...field} />}
      </FormController>

      <FormController
        control={control}
        name="address.state"
        label={t('State')}
        overrides={{ Root: { props: { className: 'col-span-2' } } }}
      >
        {({ field }) => (
          <StateSelect
            useClientCountryQuery={true}
            countryCode={selectedCountryCode}
            value={field.value}
            onChange={({ option }) => {
              field.onChange(option?.id);
            }}
          />
        )}
      </FormController>
    </form>
  );
}

type CustomerSearchType =
  | 'first-name'
  | 'last-name'
  | 'email'
  | 'phone'
  | 'search';

type CustomerResultOption = Customer & {
  searchQuery: string;
  searchType: CustomerSearchType;
};

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function ReplacementNode(props: {
  isSelected: boolean;
  option: CustomerResultOption;
}) {
  const { option } = props;

  const higlightMatched = (
    value: string,
    matcher: CustomerResultOption['searchType'],
  ) => {
    if (matcher !== option.searchType) return value;
    const escapedQuery = escapeRegExp(option.searchQuery);
    const regex = new RegExp(escapedQuery, 'gi');
    if (!regex.test(value)) return value;
    return value.replace(
      regex,
      (matched) =>
        `<span class="text-brand-primary font-bold">${matched}</span>`,
    );
  };
  return (
    <div className="px-3 py-2">
      <div className="space-x-1 text-base font-semibold text-gray-400">
        <span
          dangerouslySetInnerHTML={{
            __html: higlightMatched(option.firstName, 'first-name'),
          }}
        />
        <span
          dangerouslySetInnerHTML={{
            __html: higlightMatched(option.lastName, 'last-name'),
          }}
        />
        <span>(#{option.id})</span>
      </div>
      <div className="space-y-1 font-prompt  text-gray-500 ">
        <span
          dangerouslySetInnerHTML={{
            __html: higlightMatched(option.businessName ?? '', 'search'),
          }}
        />
        <div
          className="text-sm"
          dangerouslySetInnerHTML={{
            __html: higlightMatched(option.email, 'email'),
          }}
        />
        <div
          className="text-sm"
          dangerouslySetInnerHTML={{
            __html: higlightMatched(option.phone, 'phone'),
          }}
        />
      </div>
    </div>
  );
}

type CustomerSearchComboboxProps = {
  onChange?: (value: string) => void;
  onSelect?: (selected: Customer) => void;
  onManualInputToggled?: () => void;
  searchType?: CustomerSearchType;
  performSearch?: boolean;
};

function CustomerSearchCombobox(
  props: Omit<InputProps, keyof CustomerSearchComboboxProps> &
    CustomerSearchComboboxProps,
) {
  const { t } = useLocale();
  const {
    onChange: handleChange,
    onSelect: handleSelected,
    onManualInputToggled: onManualInputToggledHandler,
    searchType = 'first-name',
    performSearch = false,
    ...inputProps
  } = props;
  const [value, setValue] = useState('');
  const debouncedSearch = useDebounce(value);
  const customersQuery = useCustomersQuery(
    {
      params: {
        [searchType]: debouncedSearch,
      },
    },
    { skip: debouncedSearch.length === 0 },
  );

  return (
    <>
      <Combobox<CustomerResultOption>
        value={value}
        onChange={(nextValue, selectedValue) => {
          if (!performSearch && handleChange) {
            return handleChange(nextValue);
          }
          if (selectedValue && handleSelected) {
            handleSelected(selectedValue);
          }
          if (handleChange && selectedValue == null) {
            handleChange(nextValue);
            setValue(nextValue);
          }
        }}
        options={
          customersQuery.data
            ? customersQuery.data.list.map((item) => ({
                ...item,
                searchQuery: value,
                searchType,
              }))
            : []
        }
        mapOptionToString={(option) => `${option.firstName} ${option.lastName}`}
        mapOptionToNode={customersQuery.isSuccess ? ReplacementNode : undefined}
        name="input-overrides"
        overrides={{
          Input: {
            component: Input,
            props: {
              ...inputProps,
              endEnhancer: customersQuery.isFetching ? (
                <Spinner $size={16} $borderWidth={2} />
              ) : undefined,
            } as InputProps,
          },
          Root: {
            props: {
              className: 'w-full',
            },
          },
          ListItem: {
            props: {
              className: 'h-auto',
            },
          },
        }}
      />
      {customersQuery.isError && customersQuery.data === undefined ? (
        <StyledCaption $error>
          Failed to load customers, clear search and try again.
        </StyledCaption>
      ) : null}
    </>
  );
}

type CustomerAddressResultOption = Customer & {
  searchQuery: string;
};

function ReplacementNodeAddress(props: {
  isSelected: boolean;
  option: CustomerAddressResultOption;
}) {
  const { option } = props;

  const higlightMatched = (value: string) => {
    const escapedQuery = escapeRegExp(option.searchQuery);
    const regex = new RegExp(escapedQuery, 'gi');
    if (!regex.test(value)) return value;
    return value.replace(
      regex,
      (matched) =>
        `<span class="text-brand-primary font-bold">${matched}</span>`,
    );
  };
  return (
    <div className="px-3 py-2">
      <div className="space-x-1 text-base font-semibold text-gray-400">
        <span
          dangerouslySetInnerHTML={{
            __html: higlightMatched(option.firstName),
          }}
        />
        <span
          dangerouslySetInnerHTML={{
            __html: higlightMatched(option.lastName),
          }}
        />
        <span>(#{option.id})</span>
      </div>
      <div className="space-y-1 font-prompt  text-gray-500 ">
        <div
          className="text-sm"
          dangerouslySetInnerHTML={{
            __html: higlightMatched(option.address.street),
          }}
        />
        <div
          className="text-sm"
          dangerouslySetInnerHTML={{
            __html: higlightMatched(option.address.street2 ?? ''),
          }}
        />
      </div>
    </div>
  );
}

type CustomerAddressSearchComboboxProps = {
  onChange?: (value: string) => void;
  onSelect?: (selected: Customer) => void;
  onManualInputToggled?: () => void;
  performSearch?: boolean;
};

function CustomerAddressSearchCombobox(
  props: Omit<InputProps, keyof CustomerAddressSearchComboboxProps> &
    CustomerAddressSearchComboboxProps,
) {
  const { t } = useLocale();
  const {
    onChange: handleChange,
    onSelect: handleSelected,
    onManualInputToggled: onManualInputToggledHandler,
    performSearch = false,
    ...inputProps
  } = props;
  const [value, setValue] = useState('');
  const debouncedSearch = useDebounce(value);
  const skip = debouncedSearch.length === 0;
  const addressQuery = useCustomersAddressQuery(
    { params: { address: debouncedSearch } },
    { skip },
  );
  const firstNameQuery = useCustomersAddressQuery(
    { params: { 'first-name': debouncedSearch } },
    { skip },
  );
  const lastNameQuery = useCustomersAddressQuery(
    { params: { 'last-name': debouncedSearch } },
    { skip },
  );
  const mergedOptions = useMemo(() => {
    const nameItems = [
      ...(firstNameQuery.data ?? []),
      ...(lastNameQuery.data ?? []),
    ];
    const source = nameItems.length > 0 ? nameItems : (addressQuery.data ?? []);
    const seen = new Set<number>();
    const merged: Address[] = [];
    for (const item of source) {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        merged.push(item);
      }
    }
    return merged;
  }, [addressQuery.data, firstNameQuery.data, lastNameQuery.data]);
  const isFetching =
    addressQuery.isFetching ||
    firstNameQuery.isFetching ||
    lastNameQuery.isFetching;
  const isSuccess =
    addressQuery.isSuccess ||
    firstNameQuery.isSuccess ||
    lastNameQuery.isSuccess;
  const isError =
    addressQuery.isError && firstNameQuery.isError && lastNameQuery.isError;
  const hasNoData =
    !addressQuery.data && !firstNameQuery.data && !lastNameQuery.data;

  return (
    <>
      <Combobox<CustomerAddressResultOption>
        value={value}
        onChange={(nextValue, selectedValue) => {
          if (!performSearch && handleChange) {
            return handleChange(nextValue);
          }
          if (selectedValue && handleSelected) {
            handleSelected(selectedValue);
          }
          if (handleChange && selectedValue == null) {
            handleChange(nextValue);
            setValue(nextValue);
          }
        }}
        options={mergedOptions.map(
          (item) =>
            ({
              ...item,
              searchQuery: value,
            } as unknown as CustomerAddressResultOption),
        )}
        mapOptionToString={(option) =>
          `${option.address.street} ${option.address.street2}`
        }
        mapOptionToNode={isSuccess ? ReplacementNodeAddress : undefined}
        name="input-overrides"
        overrides={{
          Input: {
            component: Input,
            props: {
              ...inputProps,
              endEnhancer: isFetching ? (
                <Spinner $size={16} $borderWidth={2} />
              ) : undefined,
            } as InputProps,
          },
          Root: {
            props: {
              className: 'w-full',
            },
          },
          ListItem: {
            props: {
              className: 'h-auto',
            },
          },
        }}
      />
      {isError && hasNoData ? (
        <StyledCaption $error>
          Failed to load customer address, clear search and try again.
        </StyledCaption>
      ) : null}
    </>
  );
}

function Alert({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md bg-red-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
        </div>
        <div className="ml-3">{children}</div>
      </div>
    </div>
  );
}

function AlertTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-prompt text-sm font-medium text-red-800">{children}</h3>
  );
}

function AlertDescription({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-2 font-sans text-sm text-red-700">
      <p>{children}</p>
    </div>
  );
}

function AlertActions({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <div className="-mx-2 -my-1.5 flex">{children}</div>
    </div>
  );
}

function VerfyingAddress<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({ control, path }: { control: Control<T>; path: TName }) {
  const isVerifying = useWatch({ control, name: path });
  const { t } = useLocale();
  if (isVerifying) {
    return (
      <div className="flex items-center space-x-1 text-gray-500">
        <Spinner $size={20} $borderWidth={3} />
        <span>{t('Verifying')}</span>
      </div>
    );
  }
  return null;
}

function RHFErrorMessageValue<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  control,
  field,
  children,
}: {
  control: Control<T>;
  field: TName;
  children: (message: string) => React.ReactNode;
}) {
  const { errors } = useFormState({ control, name: field, exact: true });
  const error = get(errors, field, '');

  if (!error) {
    return null;
  }
  const { message: messageFromRegister } = error;
  return <>{children(messageFromRegister)}</>;
}

export function FieldErrorMessage<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  control,
  field,
  children,
}: {
  control: Control<T>;
  field: TName | TName[];
  children?: (message: string) => React.ReactNode;
}) {
  const { t, i18n } = useLocale();
  const { errors } = useFormState({ control, name: field, exact: true });
  const error = Array.isArray(field)
    ? field.reduce((errMessage, fieldName) => {
        if (errMessage) {
          return errMessage;
        }
        errMessage = getRHFFieldError(errors, fieldName, '');
        return errMessage;
      }, '')
    : getRHFFieldError(errors, field, '');

  if (!error) {
    return null;
  }
  const { message: messageFromRegister } = error;
  const errorMessage = messageFromRegister;
  const keyedMessage = errorMessage ? `${errorMessage}` : '';

  /**
   * The zod `message` may be passed a translation key (ex:errors.undefined) to handle custom
   * error message without `refine`.
   */
  const keyedMessageExist = keyedMessage
    ? i18n.exists(keyedMessage, {
        ns: 'zod',
      })
    : false;

  const translatedMessage = keyedMessageExist
    ? t(keyedMessage, { ns: 'zod' })
    : // This should already contain the translated message if no `keyed` message
    errorMessage
    ? errorMessage
    : t('default_validation_message', {
        ns: 'zod',
      });

  if (!children) {
    return (
      <AriaText
        data-slot="error"
        slot="errorMessage"
        className="block text-base/6 text-red-600 data-[disabled]:opacity-50 sm:text-sm/6"
      >
        {translatedMessage}
      </AriaText>
    );
  }

  return <>{children(translatedMessage)}</>;
}
